///
/// EarthBrowser
///
/// Created by Matt Giger on 10/25/2026
/// Copyright (c) 2026 Geodata Labs
///
/// One classification of an NDBC station for the globe marker, the anchored
/// panel and the embed HUD, so all three tell the same story about what a
/// station is and whether it is actually reporting.
///

/** Observation fields the classification and display compositing care about */
export interface ClassifiableObservation {
	time: string
	wind_speed: number | null
	pressure: number | null
	wave_height: number | null
	water_column_height?: number | null
	dart_measurement_type?: number | null
	water_level?: number | null
}

export interface ClassifiableStation {
	type: string | null
	dart: boolean
}

/** Attribution per ingest source, for panel footers and HUD lines */
export const cBuoySourceLabel: Record<string, string> = {
	ndbc: "NOAA NDBC",
	coops: "NOAA CO-OPS",
	imos: "IMOS (Australia)",
	wavenet: "Cefas WaveNet (UK)",
}

export function buoySourceLabel(source: string | null | undefined): string {
	return cBuoySourceLabel[source ?? "ndbc"] ?? (source ?? "NOAA NDBC")
}

/**
 * - dart: tsunameter. Classified first regardless of observations; whether
 *   it is live depends on a water-column reading (see isDartReporting)
 * - inactive: nothing reported in 30 days
 * - met: reports wind or pressure (may also report waves)
 * - wave: reports waves only
 * - tide: a tide gauge reporting water level only
 * - limited: reporting, but neither wind, pressure, waves nor water level
 */
export type BuoyCategory = "dart" | "met" | "wave" | "tide" | "limited" | "inactive"

export const cBuoyInactiveThresholdMs = 30 * 24 * 60 * 60 * 1000

/**
 * NDBC publishes partial rows (half-hourly met without waves, hourly waves
 * without met), so the newest row alone under-reports the station. Fields
 * missing from the latest row are filled from rows within this window.
 */
export const cBuoyCompositeWindowMs = 3 * 60 * 60 * 1000

export const cBuoyCategoryLabel: Record<BuoyCategory, string> = {
	dart: "DART",
	met: "Met",
	wave: "Wave",
	tide: "Tide",
	limited: "Limited",
	inactive: "Inactive",
}

export function isDartStation(station: ClassifiableStation): boolean {
	return station.dart || station.type === "dart"
}

export function isObservationStale(obs: ClassifiableObservation | null, nowMs: number = Date.now()): boolean {
	if (!obs) return true
	return nowMs - new Date(obs.time).getTime() > cBuoyInactiveThresholdMs
}

/**
 * DART stations report every 15 min in standard mode but the files can lag
 * by hours; a reading inside this window counts as live
 */
export const cDartReportingWindowMs = 24 * 60 * 60 * 1000

/** A DART tsunameter with a recent water-column height */
export function isDartReporting(obs: ClassifiableObservation | null, nowMs: number = Date.now()): boolean {
	if (obs?.water_column_height == null) return false
	return nowMs - new Date(obs.time).getTime() <= cDartReportingWindowMs
}

/** Event reporting (1-minute or 15-second mode): the tsunameter has detected a wave */
export function isDartEventMode(obs: ClassifiableObservation | null): boolean {
	return obs?.dart_measurement_type === 2 || obs?.dart_measurement_type === 3
}

export const cDartModeLabel: Record<number, string> = {
	1: "15-minute standard reporting",
	2: "Event reporting · 1-minute",
	3: "Event reporting · 15-second",
}

export function classifyBuoy(
	station: ClassifiableStation,
	obs: ClassifiableObservation | null,
	nowMs: number = Date.now()
): BuoyCategory {
	if (isDartStation(station)) return "dart"
	if (isObservationStale(obs, nowMs)) return "inactive"
	if (obs!.wind_speed != null || obs!.pressure != null) return "met"
	if (obs!.wave_height != null) return "wave"
	if (obs!.water_level != null) return "tide"
	return "limited"
}

/**
 * Fill null fields of the latest observation from recent history, newest
 * first, within the composite window. `history` may be in any order and may
 * include `latest` itself. Returns `latest` unchanged when nothing applies.
 */
export function compositeObservation<T extends { time: string }>(latest: T, history: T[]): T {
	const latestMs = new Date(latest.time).getTime()
	const cutoff = latestMs - cBuoyCompositeWindowMs
	const recent = history
		.filter(o => {
			const ms = new Date(o.time).getTime()
			return ms >= cutoff && ms <= latestMs && o !== latest
		})
		.sort((a, b) => b.time.localeCompare(a.time))
	if (recent.length === 0) return latest

	let result: T | null = null
	for (const key of Object.keys(latest) as Array<keyof T>) {
		if (latest[key] !== null) continue
		for (const o of recent) {
			if (o[key] !== null && o[key] !== undefined) {
				result ??= { ...latest }
				result[key] = o[key]
				break
			}
		}
	}
	return result ?? latest
}

/** WMO sea-state words for significant wave height (meters) */
export function waveDescription(heightM: number): string {
	if (heightM < 0.5) return "Calm"
	if (heightM < 1.25) return "Smooth"
	if (heightM < 2.5) return "Slight"
	if (heightM < 4) return "Moderate"
	if (heightM < 6) return "Rough"
	if (heightM < 9) return "Very Rough"
	if (heightM < 14) return "High"
	return "Phenomenal"
}
